import type { Role } from '@/api/types';
import { CompetitionStatus } from '@/api/types';

export interface NavItem {
  label: string;
  to: string;
  /** Match this route exactly (NavLink `end`) - for a section landing page whose path prefixes its siblings. */
  end?: boolean;
  group?: string;
}

export interface CompetitionNavContext {
  competitionId: string;
  status: CompetitionStatus;
  canManage: boolean;
  isJudge: boolean;
  isParticipant: boolean;
}

/**
 * The sidebar shows one competition at a time: the items below are built for
 * the competition the user has opened, from their role in THAT competition.
 * Entries must match the route guards in routes/AppRouter.tsx (and the
 * backend's authorization) - otherwise the sidebar either hides a page
 * someone can reach, or links to one that 403s.
 */
export function competitionNavItems(c: CompetitionNavContext): NavItem[] {
  const base = `/competitions/${c.competitionId}`;
  const items: NavItem[] = [{ label: 'Pregled tekmovanja', to: base, end: true }];

  if (c.isParticipant) {
    items.push(
      { label: 'Moji prispevki', to: '/uploads', group: 'Tekmovalec' },
      { label: 'Moji rezultati', to: '/results', group: 'Tekmovalec' },
    );
  }

  if (c.isJudge) {
    items.push({ label: 'Ocenjevanje', to: `/judge/scoring?competition=${c.competitionId}`, group: 'Sodnik' });
    // Judges must not see standings before judging is finished.
    if (c.status >= CompetitionStatus.Completed) {
      items.push({ label: 'Razvrstitev', to: '/judge/ranking', group: 'Sodnik' });
    }
  }

  if (c.canManage) {
    const org = `/organizer/competitions/${c.competitionId}`;
    items.push(
      { label: 'Pregled', to: '/organizer', end: true, group: 'Organizator' },
      { label: 'Kategorije', to: `${org}/categories`, group: 'Organizator' },
      { label: 'Udeleženci', to: `${org}/participants`, group: 'Organizator' },
      { label: 'Sodniki', to: `${org}/judges`, group: 'Organizator' },
      { label: 'Nagrade', to: `${org}/awards`, group: 'Organizator' },
      { label: 'Razpis', to: `${org}/landing`, group: 'Organizator' },
      { label: 'Plačila', to: `${org}/payments`, group: 'Organizator' },
      { label: 'Poročila', to: '/organizer/reports', group: 'Organizator' },
      { label: 'Upravljanje tekmovanj', to: '/organizer/competitions', group: 'Organizator' },
    );
  }

  return items;
}

/** Platform-wide items, only for SuperAdmin (route guard: RoleRoute allow=['SuperAdmin']). */
export function adminNavItems(roles: Role[] | undefined): NavItem[] {
  if (!(roles ?? []).includes('SuperAdmin')) return [];
  return [
    { label: 'Upravljanje tekmovanj', to: '/organizer/competitions', group: 'Skrbništvo' },
    { label: 'Uporabniki', to: '/admin/users', group: 'Skrbništvo' },
    { label: 'Organizacije', to: '/admin/organizations', group: 'Skrbništvo' },
    { label: 'Dnevnik revizije', to: '/admin/logs', group: 'Skrbništvo' },
    { label: 'Nastavitve', to: '/admin/settings', group: 'Skrbništvo' },
  ];
}
